import React, { useState, useEffect } from 'react';
import { useCvData } from '../context/CvDataContext.js';
import JsonImportExport from './JsonImportExport.js';
import GlobalBackup from './GlobalBackup.js';
import './AdminPanel.css';

const AdminPanel = () => {
  const { personalInfo, contactInfo, experience, education, patents, mainMedia, loading, error, reload } = useCvData();
  const [cvName] = useState(() => new URLSearchParams(window.location.search).get('name') || 'default');
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('about');
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  // Initialize edit data when component mounts or data changes
  useEffect(() => {
    if (personalInfo) {
      setEditData({
        personalInfo: { ...personalInfo },
        contactInfo: { ...contactInfo },
        experience: [...experience],
        education: [...education],
        patents: [...patents],
        mainMedia: [...mainMedia]
      });
    }
  }, [personalInfo, contactInfo, experience, education, patents, mainMedia]);

  const handleInputChange = (section, field, value, index = null) => {
    setEditData(prev => {
      const newData = { ...prev };
      
      if (index !== null) {
        // For arrays (experience, education, patents)
        newData[section] = [...prev[section]];
        newData[section][index] = {
          ...newData[section][index],
          [field]: value
        };
      } else {
        // For objects (personalInfo, contactInfo)
        newData[section] = {
          ...newData[section],
          [field]: value
        };
      }
      
      return newData;
    });
  };

  const handleArrayItemChange = (section, index, field, value) => {
    setEditData(prev => {
      const newData = { ...prev };
      newData[section] = [...prev[section]];
      newData[section][index] = {
        ...newData[section][index],
        [field]: value
      };
      return newData;
    });
  };

  const handleAddArrayItem = (section, newItem) => {
    setEditData(prev => {
      const newData = { ...prev };
      newData[section] = [...prev[section], newItem];
      return newData;
    });
  };

  const handleRemoveArrayItem = (section, index) => {
    setEditData(prev => {
      const newData = { ...prev };
      newData[section] = prev[section].filter((_, i) => i !== index);
      return newData;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveMessage('');
    
    try {
      // Save each section to the backend
      const sections = ['personalInfo', 'contactInfo', 'experience', 'education', 'patents', 'mainMedia'];
      
      for (const section of sections) {
        if (editData[section]) {
          const apiUrl = process.env.NODE_ENV === 'development' 
            ? 'http://localhost:8888/.netlify/functions/update-cv'
            : '/.netlify/functions/update-cv';
          
          const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              section: section,
              data: editData[section],
              cvName: cvName
            })
          });
          
          if (!response.ok) {
            throw new Error(`Failed to update ${section}`);
          }
        }
      }
      
      setSaveMessage('Changes saved successfully!');
      setIsEditing(false);
      
      // Reload data to reflect changes
      reload();
      
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      setSaveMessage('Error saving changes. Please try again.');
      console.error('Save error:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditData({
      personalInfo: { ...personalInfo },
      contactInfo: { ...contactInfo },
      experience: [...experience],
      education: [...education],
      patents: [...patents],
      mainMedia: [...mainMedia]
    });
    setIsEditing(false);
    setSaveMessage('');
  };

  if (loading) return <div className="admin-panel">Loading admin panel...</div>;
  if (error) return <div className="admin-panel">Error loading data: {error.message}</div>;

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <h2>Portfolio Admin Panel</h2>
        <div className="admin-controls">
          {!isEditing ? (
            <button 
              className="btn btn-primary"
              onClick={() => setIsEditing(true)}
            >
              Edit Portfolio
            </button>
          ) : (
            <div className="edit-controls">
              <button 
                className="btn btn-success"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              <button 
                className="btn btn-secondary"
                onClick={handleCancel}
                disabled={saving}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>

      {saveMessage && (
        <div className={`save-message ${saveMessage.includes('Error') ? 'error' : 'success'}`}>
          {saveMessage}
        </div>
      )}

      <div className="admin-tabs">
        <button 
          className={`tab ${activeTab === 'about' ? 'active' : ''}`}
          onClick={() => setActiveTab('about')}
        >
          About Me
        </button>
        <button 
          className={`tab ${activeTab === 'contact' ? 'active' : ''}`}
          onClick={() => setActiveTab('contact')}
        >
          Contact Info
        </button>
        <button 
          className={`tab ${activeTab === 'experience' ? 'active' : ''}`}
          onClick={() => setActiveTab('experience')}
        >
          Experience
        </button>
        <button 
          className={`tab ${activeTab === 'education' ? 'active' : ''}`}
          onClick={() => setActiveTab('education')}
        >
          Education
        </button>
        <button 
          className={`tab ${activeTab === 'patents' ? 'active' : ''}`}
          onClick={() => setActiveTab('patents')}
        >
          Patents
        </button>
        <button 
          className={`tab ${activeTab === 'media' ? 'active' : ''}`}
          onClick={() => setActiveTab('media')}
        >
          Media Gallery
        </button>
      </div>

      <div className="admin-content">
        <div className="cv-management-section">
          <h3>CV Management</h3>
          <p>Manage multiple CVs, create new ones, and switch between them.</p>
          <a 
            href="?cv-management=true" 
            className="cv-management-link"
          >
            <i className="fas fa-users-cog"></i>
            Go to CV Management
          </a>
        </div>
        <GlobalBackup isEditing={isEditing} />
        {activeTab === 'about' && (
          <div className="edit-section">
            <h3>About Me Section</h3>
            
            <JsonImportExport 
              category="personalInfo"
              data={editData.personalInfo}
              onDataUpdate={(data) => setEditData(prev => ({ ...prev, personalInfo: data }))}
              isEditing={isEditing}
            />
            
            <div className="form-group">
              <label htmlFor="name">Name:</label>
              <input
                type="text"
                id="name"
                value={editData.personalInfo?.name || ''}
                onChange={(e) => handleInputChange('personalInfo', 'name', e.target.value)}
                disabled={!isEditing}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label htmlFor="title">Title:</label>
              <input
                type="text"
                id="title"
                value={editData.personalInfo?.title || ''}
                onChange={(e) => handleInputChange('personalInfo', 'title', e.target.value)}
                disabled={!isEditing}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label htmlFor="avatar">Avatar (initials):</label>
              <input
                type="text"
                id="avatar"
                value={editData.personalInfo?.avatar || ''}
                onChange={(e) => handleInputChange('personalInfo', 'avatar', e.target.value)}
                disabled={!isEditing}
                className="form-input"
                maxLength="2"
              />
            </div>
            <div className="form-group">
              <label htmlFor="summary">Summary:</label>
              <textarea
                id="summary"
                value={editData.personalInfo?.summary || ''}
                onChange={(e) => handleInputChange('personalInfo', 'summary', e.target.value)}
                disabled={!isEditing}
                className="form-textarea"
                rows="6"
                placeholder="Write your professional summary here..."
              />
            </div>
          </div>
        )}

        {activeTab === 'contact' && (
          <div className="edit-section">
            <h3>Contact Information</h3>
            
            <JsonImportExport 
              category="contactInfo"
              data={editData.contactInfo}
              onDataUpdate={(data) => setEditData(prev => ({ ...prev, contactInfo: data }))}
              isEditing={isEditing}
            />
            
            <div className="form-group">
              <label htmlFor="email">Email:</label>
              <input
                type="email"
                id="email"
                value={editData.contactInfo?.email || ''}
                onChange={(e) => handleInputChange('contactInfo', 'email', e.target.value)}
                disabled={!isEditing}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label htmlFor="phone">Phone:</label>
              <input
                type="tel"
                id="phone"
                value={editData.contactInfo?.phone || ''}
                onChange={(e) => handleInputChange('contactInfo', 'phone', e.target.value)}
                disabled={!isEditing}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label htmlFor="whatsapp">WhatsApp:</label>
              <input
                type="tel"
                id="whatsapp"
                value={editData.contactInfo?.whatsapp || ''}
                onChange={(e) => handleInputChange('contactInfo', 'whatsapp', e.target.value)}
                disabled={!isEditing}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label htmlFor="linkedin">LinkedIn URL:</label>
              <input
                type="url"
                id="linkedin"
                value={editData.contactInfo?.linkedin || ''}
                onChange={(e) => handleInputChange('contactInfo', 'linkedin', e.target.value)}
                disabled={!isEditing}
                className="form-input"
              />
            </div>
          </div>
        )}

        {activeTab === 'experience' && (
          <div className="edit-section">
            <h3>Work Experience</h3>
            
            <JsonImportExport 
              category="experience"
              data={editData.experience}
              onDataUpdate={(data) => setEditData(prev => ({ ...prev, experience: data }))}
              isEditing={isEditing}
            />
            
            <div className="experience-list">
              {editData.experience?.map((exp, index) => (
                <div key={exp.id} className="experience-item">
                  <div className="experience-header">
                    <h4>{exp.company || 'Untitled Company'}</h4>
                    <span className="experience-id">ID: {exp.id}</span>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label>Company:</label>
                      <input
                        type="text"
                        value={exp.company || ''}
                        onChange={(e) => handleArrayItemChange('experience', index, 'company', e.target.value)}
                        disabled={!isEditing}
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <label>Title:</label>
                      <input
                        type="text"
                        value={exp.title || ''}
                        onChange={(e) => handleArrayItemChange('experience', index, 'title', e.target.value)}
                        disabled={!isEditing}
                        className="form-input"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Period:</label>
                      <input
                        type="text"
                        value={exp.period || ''}
                        onChange={(e) => handleArrayItemChange('experience', index, 'period', e.target.value)}
                        disabled={!isEditing}
                        className="form-input"
                        placeholder="e.g., 2021 - Present"
                      />
                    </div>
                    <div className="form-group">
                      <label>Location:</label>
                      <input
                        type="text"
                        value={exp.location || ''}
                        onChange={(e) => handleArrayItemChange('experience', index, 'location', e.target.value)}
                        disabled={!isEditing}
                        className="form-input"
                        placeholder="e.g., Tel Aviv, Israel"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Website:</label>
                      <input
                        type="url"
                        value={exp.website || ''}
                        onChange={(e) => handleArrayItemChange('experience', index, 'website', e.target.value)}
                        disabled={!isEditing}
                        className="form-input"
                        placeholder="https://company.com"
                      />
                    </div>
                    <div className="form-group">
                      <label>Type:</label>
                      <select
                        value={exp.type || 'Full-time'}
                        onChange={(e) => handleArrayItemChange('experience', index, 'type', e.target.value)}
                        disabled={!isEditing}
                        className="form-select"
                      >
                        <option value="Full-time">Full-time</option>
                        <option value="Part-time">Part-time</option>
                        <option value="Contract">Contract</option>
                        <option value="Consulting">Consulting</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Description (Overview):</label>
                    <textarea
                      value={exp.description || ''}
                      onChange={(e) => handleArrayItemChange('experience', index, 'description', e.target.value)}
                      disabled={!isEditing}
                      className="form-textarea"
                      rows="4"
                      placeholder="Describe your role and responsibilities..."
                    />
                  </div>

                  <div className="form-group">
                    <label>Impact:</label>
                    <textarea
                      value={exp.impact || ''}
                      onChange={(e) => handleArrayItemChange('experience', index, 'impact', e.target.value)}
                      disabled={!isEditing}
                      className="form-textarea"
                      rows="3"
                      placeholder="Describe the impact and results of your work..."
                    />
                  </div>

                  <div className="form-group">
                    <label>Technologies Used (comma-separated):</label>
                    <input
                      type="text"
                      value={exp.technologies?.join(', ') || ''}
                      onChange={(e) => {
                        const technologies = e.target.value.split(',').map(tech => tech.trim()).filter(tech => tech);
                        handleArrayItemChange('experience', index, 'technologies', technologies);
                      }}
                      disabled={!isEditing}
                      className="form-input"
                      placeholder="React, Node.js, Python, AWS..."
                    />
                  </div>

                  <div className="form-group">
                    <label>Key Achievements (one per line):</label>
                    <textarea
                      value={exp.achievements?.join('\n') || ''}
                      onChange={(e) => {
                        const achievements = e.target.value.split('\n').filter(achievement => achievement.trim());
                        handleArrayItemChange('experience', index, 'achievements', achievements);
                      }}
                      disabled={!isEditing}
                      className="form-textarea"
                      rows="6"
                      placeholder="Led a team of 5 developers...&#10;Increased performance by 40%...&#10;Implemented new architecture..."
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Logo URL:</label>
                      <input
                        type="url"
                        value={exp.logo || ''}
                        onChange={(e) => handleArrayItemChange('experience', index, 'logo', e.target.value)}
                        disabled={!isEditing}
                        className="form-input"
                        placeholder="/logos/company.png"
                      />
                    </div>
                    <div className="form-group">
                      <label>Hero Image URL:</label>
                      <input
                        type="url"
                        value={exp.hero || ''}
                        onChange={(e) => handleArrayItemChange('experience', index, 'hero', e.target.value)}
                        disabled={!isEditing}
                        className="form-input"
                        placeholder="/heroes/company.svg"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Local Hero Image URL:</label>
                    <input
                      type="url"
                      value={exp.heroLocal || ''}
                      onChange={(e) => handleArrayItemChange('experience', index, 'heroLocal', e.target.value)}
                      disabled={!isEditing}
                      className="form-input"
                      placeholder="/company_images/company.webp"
                    />
                  </div>

                  <div className="media-section">
                    <h5>Media (Images & Videos)</h5>
                    <div className="media-preview">
                      <div className="media-images">
                        <h6>Images ({exp.media?.images?.length || 0})</h6>
                        {exp.media?.images?.map((img, imgIndex) => (
                          <div key={imgIndex} className="media-item">
                            <img src={img.src} alt={img.alt || ''} className="media-thumbnail" />
                            <div className="media-info">
                              <input
                                type="text"
                                value={img.src || ''}
                                onChange={(e) => {
                                  const newImages = [...(exp.media?.images || [])];
                                  newImages[imgIndex] = { ...newImages[imgIndex], src: e.target.value };
                                  handleArrayItemChange('experience', index, 'media', { ...exp.media, images: newImages });
                                }}
                                disabled={!isEditing}
                                className="form-input small"
                                placeholder="Image URL"
                              />
                              <input
                                type="text"
                                value={img.alt || ''}
                                onChange={(e) => {
                                  const newImages = [...(exp.media?.images || [])];
                                  newImages[imgIndex] = { ...newImages[imgIndex], alt: e.target.value };
                                  handleArrayItemChange('experience', index, 'media', { ...exp.media, images: newImages });
                                }}
                                disabled={!isEditing}
                                className="form-input small"
                                placeholder="Alt text"
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      <div className="media-videos">
                        <h6>Videos ({exp.media?.videos?.length || 0})</h6>
                        {exp.media?.videos?.map((video, videoIndex) => (
                          <div key={videoIndex} className="media-item">
                            <div className="media-thumbnail video-thumbnail">
                              {video.thumbnail ? (
                                <img src={video.thumbnail} alt={video.title || ''} />
                              ) : (
                                <div className="video-placeholder">📹</div>
                              )}
                            </div>
                            <div className="media-info">
                              <input
                                type="text"
                                value={video.src || ''}
                                onChange={(e) => {
                                  const newVideos = [...(exp.media?.videos || [])];
                                  newVideos[videoIndex] = { ...newVideos[videoIndex], src: e.target.value };
                                  handleArrayItemChange('experience', index, 'media', { ...exp.media, videos: newVideos });
                                }}
                                disabled={!isEditing}
                                className="form-input small"
                                placeholder="Video URL"
                              />
                              <input
                                type="text"
                                value={video.title || ''}
                                onChange={(e) => {
                                  const newVideos = [...(exp.media?.videos || [])];
                                  newVideos[videoIndex] = { ...newVideos[videoIndex], title: e.target.value };
                                  handleArrayItemChange('experience', index, 'media', { ...exp.media, videos: newVideos });
                                }}
                                disabled={!isEditing}
                                className="form-input small"
                                placeholder="Video title"
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  {isEditing && (
                    <button
                      onClick={() => handleRemoveArrayItem('experience', index)}
                      className="remove-button"
                    >
                      <i className="fas fa-trash"></i> Remove Experience
                    </button>
                  )}
                </div>
              ))}
              
              {isEditing && (
                <button
                  onClick={() => handleAddArrayItem('experience', {
                    id: Date.now(),
                    title: '',
                    company: '',
                    period: '',
                    location: '',
                    website: '',
                    type: 'Full-time',
                    description: '',
                    impact: '',
                    technologies: [],
                    achievements: [],
                    logo: '',
                    hero: '',
                    heroLocal: '',
                    media: { images: [], videos: [] }
                  })}
                  className="add-button"
                >
                  <i className="fas fa-plus"></i> Add Experience
                </button>
              )}
            </div>
          </div>
        )}

        {activeTab === 'education' && (
          <div className="edit-section">
            <h3>Education</h3>
            
            <JsonImportExport 
              category="education"
              data={editData.education}
              onDataUpdate={(data) => setEditData(prev => ({ ...prev, education: data }))}
              isEditing={isEditing}
            />
            
            <div className="education-list">
              {editData.education?.map((edu, index) => (
                <div key={edu.id} className="education-item">
                  <div className="form-group">
                    <label>Degree:</label>
                    <input
                      type="text"
                      value={edu.degree || ''}
                      onChange={(e) => handleArrayItemChange('education', index, 'degree', e.target.value)}
                      disabled={!isEditing}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label>Institution:</label>
                    <input
                      type="text"
                      value={edu.institution || ''}
                      onChange={(e) => handleArrayItemChange('education', index, 'institution', e.target.value)}
                      disabled={!isEditing}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label>Period:</label>
                    <input
                      type="text"
                      value={edu.period || ''}
                      onChange={(e) => handleArrayItemChange('education', index, 'period', e.target.value)}
                      disabled={!isEditing}
                      className="form-input"
                    />
                  </div>
                  
                  {isEditing && (
                    <button
                      onClick={() => handleRemoveArrayItem('education', index)}
                      className="remove-button"
                    >
                      <i className="fas fa-trash"></i> Remove Education
                    </button>
                  )}
                </div>
              ))}
              
              {isEditing && (
                <button
                  onClick={() => handleAddArrayItem('education', {
                    id: Date.now(),
                    degree: '',
                    institution: '',
                    period: '',
                    description: '',
                    media: { images: [], videos: [] }
                  })}
                  className="add-button"
                >
                  <i className="fas fa-plus"></i> Add Education
                </button>
              )}
            </div>
          </div>
        )}

        {activeTab === 'patents' && (
          <div className="edit-section">
            <h3>Patents</h3>
            
            <JsonImportExport 
              category="patents"
              data={editData.patents}
              onDataUpdate={(data) => setEditData(prev => ({ ...prev, patents: data }))}
              isEditing={isEditing}
            />
            
            <div className="patents-list">
              {editData.patents?.map((patent, index) => (
                <div key={patent.id} className="patent-item">
                  <div className="form-group">
                    <label>Patent Number:</label>
                    <input
                      type="text"
                      value={patent.number || ''}
                      onChange={(e) => handleArrayItemChange('patents', index, 'number', e.target.value)}
                      disabled={!isEditing}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label>Title:</label>
                    <input
                      type="text"
                      value={patent.title || ''}
                      onChange={(e) => handleArrayItemChange('patents', index, 'title', e.target.value)}
                      disabled={!isEditing}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label>Status:</label>
                    <select
                      value={patent.status || ''}
                      onChange={(e) => handleArrayItemChange('patents', index, 'status', e.target.value)}
                      disabled={!isEditing}
                      className="form-select"
                    >
                      <option value="Granted">Granted</option>
                      <option value="Published">Published</option>
                      <option value="Pending">Pending</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Issued Date:</label>
                    <input
                      type="text"
                      value={patent.issued || ''}
                      onChange={(e) => handleArrayItemChange('patents', index, 'issued', e.target.value)}
                      disabled={!isEditing}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label>Google Patents URL:</label>
                    <input
                      type="url"
                      value={patent.url || ''}
                      onChange={(e) => handleArrayItemChange('patents', index, 'url', e.target.value)}
                      disabled={!isEditing}
                      className="form-input"
                      placeholder="https://patents.google.com/patent/US12345678A1"
                    />
                  </div>
                  
                  {isEditing && (
                    <button
                      onClick={() => handleRemoveArrayItem('patents', index)}
                      className="remove-button"
                    >
                      <i className="fas fa-trash"></i> Remove Patent
                    </button>
                  )}
                </div>
              ))}
              
              {isEditing && (
                <button
                  onClick={() => handleAddArrayItem('patents', {
                    id: Date.now(),
                    number: '',
                    title: '',
                    status: '',
                    issued: '',
                    url: '',
                    media: { images: [], videos: [] }
                  })}
                  className="add-button"
                >
                  <i className="fas fa-plus"></i> Add Patent
                </button>
              )}
            </div>
          </div>
        )}

        {activeTab === 'media' && (
          <div className="edit-section">
            <h3>Media Gallery</h3>
            
            <JsonImportExport 
              category="mainMedia"
              data={editData.mainMedia}
              onDataUpdate={(data) => setEditData(prev => ({ ...prev, mainMedia: data }))}
              isEditing={isEditing}
            />
            
            <div className="media-list">
              {editData.mainMedia?.map((media, index) => (
                <div key={media.id || index} className="media-item">
                  <div className="media-header">
                    <h4>{media.title || 'Untitled Media'}</h4>
                    <span className="media-id">ID: {media.id || index + 1}</span>
                  </div>
                  
                  <div className="form-group">
                    <label>Title:</label>
                    <input
                      type="text"
                      value={media.title || ''}
                      onChange={(e) => handleArrayItemChange('mainMedia', index, 'title', e.target.value)}
                      disabled={!isEditing}
                      className="form-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Description:</label>
                    <textarea
                      value={media.description || ''}
                      onChange={(e) => handleArrayItemChange('mainMedia', index, 'description', e.target.value)}
                      disabled={!isEditing}
                      className="form-textarea"
                      rows="3"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Media Type:</label>
                    <select
                      value={media.type || 'image'}
                      onChange={(e) => handleArrayItemChange('mainMedia', index, 'type', e.target.value)}
                      disabled={!isEditing}
                      className="form-select"
                    >
                      <option value="image">Image</option>
                      <option value="video">Video</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label>Media URL:</label>
                    <input
                      type="url"
                      value={media.src || ''}
                      onChange={(e) => handleArrayItemChange('mainMedia', index, 'src', e.target.value)}
                      disabled={!isEditing}
                      className="form-input"
                      placeholder="https://example.com/media.jpg"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Alt Text:</label>
                    <input
                      type="text"
                      value={media.alt || ''}
                      onChange={(e) => handleArrayItemChange('mainMedia', index, 'alt', e.target.value)}
                      disabled={!isEditing}
                      className="form-input"
                      placeholder="Description for accessibility"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Caption:</label>
                    <input
                      type="text"
                      value={media.caption || ''}
                      onChange={(e) => handleArrayItemChange('mainMedia', index, 'caption', e.target.value)}
                      disabled={!isEditing}
                      className="form-input"
                      placeholder="Optional caption"
                    />
                  </div>
                  
                  {isEditing && (
                    <button
                      onClick={() => handleRemoveArrayItem('mainMedia', index)}
                      className="remove-button"
                    >
                      <i className="fas fa-trash"></i> Remove Media
                    </button>
                  )}
                </div>
              ))}
              
              {isEditing && (
                <button
                  onClick={() => handleAddArrayItem('mainMedia', {
                    id: Date.now(),
                    title: '',
                    description: '',
                    type: 'image',
                    src: '',
                    alt: '',
                    caption: ''
                  })}
                  className="add-button"
                >
                  <i className="fas fa-plus"></i> Add Media Item
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
